import { type FormEvent, useCallback, useEffect, useState } from "react";
import { useParams } from "wouter";

import { apiClient } from "../api/client.js";
import { useAuth } from "../auth/AuthContext.js";
import { StripeSetupHelpButton, StripeSetupHelpModal } from "../components/StripeSetupHelpModal.js";

interface Fleet {
  id: string;
  name: string;
  slug: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface FleetMember {
  id: string;
  user_id: string;
  email: string;
  display_name: string;
  role: string;
  created_at: string;
}

interface FleetStripeSettings {
  publishable_key: string | null;
  secret_key_configured: boolean;
  managed_by_platform: boolean;
  updated_at?: string;
}

interface Venue {
  id: string;
  name: string;
  slug: string;
  status: string;
  created_at: string;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function statusBadge(status: string) {
  switch (status) {
    case "active":
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-green-500/20 text-green-400">
          Active
        </span>
      );
    case "inactive":
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-500/20 text-gray-400">
          Inactive
        </span>
      );
    default:
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-500/20 text-gray-400">
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      );
  }
}

function roleBadge(role: string) {
  switch (role) {
    case "admin":
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-blue-500/20 text-blue-400">
          Admin
        </span>
      );
    case "member":
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-500/20 text-gray-400">
          Member
        </span>
      );
    default:
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-500/20 text-gray-400">
          {role.charAt(0).toUpperCase() + role.slice(1)}
        </span>
      );
  }
}

function configuredChip(configured: boolean) {
  return configured ? (
    <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-green-500/20 text-green-400">
      Configured
    </span>
  ) : (
    <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-500/20 text-gray-400">
      Not configured
    </span>
  );
}

function OverviewSection({
  fleet,
  canAdmin,
  onUpdated,
}: {
  fleet: Fleet;
  canAdmin: boolean;
  onUpdated: () => void;
}) {
  const [nameDraft, setNameDraft] = useState(fleet.name);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setNameDraft(fleet.name);
  }, [fleet.name]);

  const handleSave = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!nameDraft.trim() || nameDraft.trim() === fleet.name) return;

      setSaving(true);
      setError(null);

      try {
        const res = await apiClient["/api/v1/admin/fleets/{id}"]["patch"]({
          parameters: { id: fleet.id },
          body: { name: nameDraft.trim() },
        });
        if (res.code === 200) {
          onUpdated();
        } else {
          const body = res.body as { message?: string };
          setError(body.message ?? "Failed to update fleet");
        }
      } catch {
        setError("Network error");
      } finally {
        setSaving(false);
      }
    },
    [fleet.id, fleet.name, nameDraft, onUpdated],
  );

  return (
    <div className="bg-surface-raised border border-border rounded-lg p-6 mb-6">
      <div className="flex items-center gap-3 mb-2">
        <h1 className="text-2xl font-bold text-text-primary">{fleet.name}</h1>
        {statusBadge(fleet.status)}
      </div>
      <div className="text-sm text-text-muted space-y-1 mb-4">
        <p>
          Slug: <span className="font-mono text-text-secondary">{fleet.slug}</span>
        </p>
        <p>
          Created: <span className="text-text-secondary">{formatDate(fleet.created_at)}</span>
        </p>
      </div>

      {canAdmin && (
        <form onSubmit={handleSave} className="flex items-end gap-3">
          <div className="flex-1 max-w-sm">
            <label className="block text-sm text-text-secondary mb-1">Rename Fleet</label>
            <input
              type="text"
              value={nameDraft}
              onChange={(e) => {
                setNameDraft(e.target.value);
                setError(null);
              }}
              required
              className="w-full px-3 py-2 rounded-md bg-surface border border-border text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent transition-colors"
            />
          </div>
          <button
            type="submit"
            disabled={saving || !nameDraft.trim() || nameDraft.trim() === fleet.name}
            className="px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Saving..." : "Save"}
          </button>
          {error && <p className="text-sm text-red-400">{error}</p>}
        </form>
      )}
    </div>
  );
}

function MembersSection({ fleetId, canAdmin }: { fleetId: string; canAdmin: boolean }) {
  const [members, setMembers] = useState<FleetMember[]>([]);
  const [loading, setLoading] = useState(true);

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const fetchMembers = useCallback(async () => {
    try {
      const res = await apiClient["/api/v1/admin/fleets/{id}/members"]["get"]({
        parameters: { id: fleetId },
        body: null,
      });
      if (res.code === 200) {
        setMembers(res.body as FleetMember[]);
      }
    } catch {
      // Ignore
    } finally {
      setLoading(false);
    }
  }, [fleetId]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleInvite = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!inviteEmail.trim()) return;

      setInviting(true);
      setInviteError(null);
      setInviteSuccess(null);

      try {
        const res = await apiClient["/api/v1/admin/fleets/{id}/members"]["post"]({
          parameters: { id: fleetId },
          body: { email: inviteEmail.trim(), role: inviteRole },
        });
        if (res.code === 201) {
          setInviteSuccess(`Invited ${inviteEmail.trim()}`);
          setInviteEmail("");
          setInviteRole("member");
          fetchMembers();
        } else {
          const body = res.body as { message?: string };
          setInviteError(body.message ?? "Failed to invite member");
        }
      } catch {
        setInviteError("Network error");
      } finally {
        setInviting(false);
      }
    },
    [fleetId, inviteEmail, inviteRole, fetchMembers],
  );

  const handleRemove = useCallback(
    async (memberId: string) => {
      setRemoveError(null);
      try {
        const res = await apiClient["/api/v1/admin/fleets/{id}/members/{memberId}"]["delete"]({
          parameters: { id: fleetId, memberId },
          body: null,
        });
        if (res.code !== 200) {
          const body = res.body as { message?: string };
          setRemoveError(body.message ?? "Failed to remove member");
        } else {
          fetchMembers();
        }
      } catch {
        setRemoveError("Network error");
      }
    },
    [fleetId, fetchMembers],
  );

  return (
    <section className="mb-6">
      <h2 className="text-lg font-semibold text-text-primary mb-4">Members</h2>

      {canAdmin && (
        <form onSubmit={handleInvite} className="flex items-end gap-3 mb-4">
          <div className="flex-1">
            <label className="block text-sm text-text-secondary mb-1">Invite Member</label>
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => {
                setInviteEmail(e.target.value);
                setInviteError(null);
                setInviteSuccess(null);
                setRemoveError(null);
              }}
              placeholder="user@example.com"
              required
              className="w-full px-3 py-2 rounded-md bg-surface border border-border text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent transition-colors"
            />
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Role</label>
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              className="px-3 py-2 rounded-md bg-surface border border-border text-text-primary focus:outline-none focus:border-accent transition-colors"
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <button
            type="submit"
            disabled={inviting || !inviteEmail.trim()}
            className="px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {inviting ? "Inviting..." : "Invite"}
          </button>
        </form>
      )}

      {inviteError && <p className="text-sm text-red-400 mb-3">{inviteError}</p>}
      {inviteSuccess && <p className="text-sm text-green-400 mb-3">{inviteSuccess}</p>}
      {removeError && <p className="text-sm text-red-400 mb-3">{removeError}</p>}

      {loading && <p className="text-text-muted text-sm">Loading members...</p>}

      {!loading && members.length === 0 && (
        <div className="bg-surface-raised border border-border rounded-lg p-6 text-center">
          <p className="text-text-secondary">No members yet.</p>
        </div>
      )}

      {!loading && members.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-border">
                <th className="pb-2 text-sm font-medium text-text-muted">Email</th>
                <th className="pb-2 text-sm font-medium text-text-muted">Display Name</th>
                <th className="pb-2 text-sm font-medium text-text-muted">Role</th>
                <th className="pb-2 text-sm font-medium text-text-muted">Joined</th>
                {canAdmin && <th className="pb-2 text-sm font-medium text-text-muted"></th>}
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id} className="border-b border-border/50">
                  <td className="py-3">
                    <span className="text-sm text-text-primary">{m.email}</span>
                  </td>
                  <td className="py-3">
                    <span className="text-sm text-text-secondary">{m.display_name || "—"}</span>
                  </td>
                  <td className="py-3">{roleBadge(m.role)}</td>
                  <td className="py-3">
                    <span className="text-sm text-text-muted">{formatDate(m.created_at)}</span>
                  </td>
                  {canAdmin && (
                    <td className="py-3 text-right">
                      <button
                        onClick={() => handleRemove(m.id)}
                        className="text-sm text-red-400 hover:text-red-300 transition-colors"
                      >
                        Remove
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function formatSendStamp(iso: string, timeZone: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-GB", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    })
      .formatToParts(date)
      .map((part) => [part.type, part.value]),
  );
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute} ${timeZone}`;
}

interface FleetReportRecipient {
  id: string;
  email: string;
  created_at: string;
}

interface FleetReports {
  enabled: boolean;
  timezone: string;
  recipients: FleetReportRecipient[];
  last_send: {
    send_date: string;
    trigger: string;
    status: string;
    period_from: string;
    period_to: string;
    error?: string | null;
    updated_at: string;
  } | null;
}

function ReportsSection({ fleetId, canAdmin }: { fleetId: string; canAdmin: boolean }) {
  const [reports, setReports] = useState<FleetReports | null>(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [sendingId, setSendingId] = useState<string | null>(null);

  const fetchReports = useCallback(async () => {
    try {
      const res = await apiClient["/api/v1/admin/fleets/{id}/reports"]["get"]({
        parameters: { id: fleetId },
        body: null,
      });
      if (res.code === 200) setReports(res.body as FleetReports);
    } catch {
      setError("Could not load report settings");
    } finally {
      setLoading(false);
    }
  }, [fleetId]);

  useEffect(() => {
    void fetchReports();
  }, [fetchReports]);

  async function toggleEnabled() {
    if (!reports) return;
    setSaving(true);
    setError(null);
    try {
      const res = await apiClient["/api/v1/admin/fleets/{id}/reports"]["patch"]({
        parameters: { id: fleetId },
        body: { enabled: !reports.enabled },
      });
      if (res.code === 200) setReports(res.body as FleetReports);
      else setError("Could not update the schedule");
    } catch {
      setError("Could not update the schedule");
    } finally {
      setSaving(false);
    }
  }

  async function addRecipient(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const res = await apiClient["/api/v1/admin/fleets/{id}/reports/recipients"]["post"]({
        parameters: { id: fleetId },
        body: { email },
      });
      if (res.code === 201) {
        setEmail("");
        await fetchReports();
      } else if (res.code === 409) {
        setError("That address already receives this fleet's reports");
      } else {
        setError("Could not add that address");
      }
    } catch {
      setError("Could not add that address");
    } finally {
      setSaving(false);
    }
  }

  async function removeRecipient(recipientId: string) {
    setError(null);
    try {
      const res = await apiClient["/api/v1/admin/fleets/{id}/reports/recipients/{recipientId}"][
        "delete"
      ]({
        parameters: { id: fleetId, recipientId },
        body: null,
      });
      if (res.code === 200) await fetchReports();
      else setError("Could not remove that address");
    } catch {
      setError("Could not remove that address");
    }
  }

  async function sendNow(recipientId: string) {
    setSendingId(recipientId);
    setError(null);
    try {
      const res = await apiClient["/api/v1/admin/fleets/{id}/reports/send"]["post"]({
        parameters: { id: fleetId, recipient_id: recipientId },
        body: null,
      });
      if (res.code === 200) await fetchReports();
      else setError("message" in res.body ? res.body.message : "Could not send the report");
    } catch {
      setError("Could not send the report");
    } finally {
      setSendingId(null);
    }
  }

  return (
    <section className="mb-6">
      <h2 className="text-lg font-semibold text-text-primary mb-2">Payments emails</h2>
      {loading && <p className="text-text-muted text-sm">Loading report settings...</p>}
      {reports && (
        <>
          <label className="flex items-start gap-2 mb-4 text-sm">
            <input
              type="checkbox"
              checked={reports.enabled}
              disabled={!canAdmin || saving}
              onChange={() => void toggleEnabled()}
              className="mt-1"
            />
            <span>
              <span className="text-text-primary">Send weekday emails</span>
              <span className="text-text-secondary">
                {" "}
                — Every weekday at 08:00 {reports.timezone}, email the last 7 complete days as a
                payments CSV (table × venue × day).
              </span>
            </span>
          </label>
          {canAdmin && (
            <form
              onSubmit={(event) => void addRecipient(event)}
              className="flex items-end gap-3 mb-4"
            >
              <div className="flex-1">
                <label className="block text-sm text-text-secondary mb-1">Recipient email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="accounts@example.com"
                  required
                  className="w-full px-3 py-2 rounded-md bg-surface border border-border text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent transition-colors"
                />
              </div>
              <button
                type="submit"
                disabled={saving || !email.trim()}
                className="px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add
              </button>
            </form>
          )}
          {error && <p className="text-sm text-red-400 mb-3">{error}</p>}
          {reports.recipients.length === 0 ? (
            <p className="text-sm text-text-secondary mb-3">No recipients yet.</p>
          ) : (
            <ul className="mb-4">
              {reports.recipients.map((recipient) => (
                <li
                  key={recipient.id}
                  className="flex items-center justify-between py-2 border-b border-border/50"
                >
                  <span className="text-sm text-text-primary">{recipient.email}</span>
                  {canAdmin && (
                    <span className="flex items-center gap-4">
                      <button
                        type="button"
                        onClick={() => void sendNow(recipient.id)}
                        disabled={sendingId !== null}
                        className="text-sm text-accent hover:text-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {sendingId === recipient.id
                          ? "Sending..."
                          : "Send last 7 complete days now"}
                      </button>
                      <button
                        type="button"
                        onClick={() => void removeRecipient(recipient.id)}
                        disabled={sendingId !== null}
                        className="text-sm text-red-400 hover:text-red-300 transition-colors disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {reports.last_send && (
            <p className="text-sm text-text-muted mb-3">
              Last send ({reports.last_send.trigger}): {reports.last_send.status} on{" "}
              {formatSendStamp(reports.last_send.updated_at, reports.timezone)}
              {reports.last_send.error ? ` — ${reports.last_send.error}` : ""}
            </p>
          )}
        </>
      )}
    </section>
  );
}

function StripeSection({ fleetId, canAdmin }: { fleetId: string; canAdmin: boolean }) {
  const [settings, setSettings] = useState<FleetStripeSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [helpOpen, setHelpOpen] = useState(false);

  const [publishableKey, setPublishableKey] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const stripeHeader = (
    <div className="mb-4 flex flex-wrap items-center gap-3">
      <h2 className="text-lg font-semibold text-text-primary">Stripe Settings</h2>
      <StripeSetupHelpButton onClick={() => setHelpOpen(true)} />
    </div>
  );

  const fetchSettings = useCallback(async () => {
    try {
      const res = await apiClient["/api/v1/admin/fleets/{id}/stripe"]["get"]({
        parameters: { id: fleetId },
        body: null,
      });
      if (res.code === 200) {
        setSettings(res.body as FleetStripeSettings);
      }
    } catch {
      // Ignore
    } finally {
      setLoading(false);
    }
  }, [fleetId]);

  useEffect(() => {
    if (canAdmin) {
      fetchSettings();
    } else {
      setLoading(false);
    }
  }, [canAdmin, fetchSettings]);

  const handleSave = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const body: { publishable_key?: string; secret_key?: string } = {};
      if (publishableKey.trim()) body.publishable_key = publishableKey.trim();
      if (secretKey.trim()) body.secret_key = secretKey.trim();

      if (Object.keys(body).length === 0) return;

      setSaving(true);
      setError(null);
      setSuccess(false);

      try {
        const res = await apiClient["/api/v1/admin/fleets/{id}/stripe"]["put"]({
          parameters: { id: fleetId },
          body,
        });
        if (res.code === 200) {
          setSettings(res.body as FleetStripeSettings);
          setPublishableKey("");
          setSecretKey("");
          setSuccess(true);
        } else {
          const resBody = res.body as { message?: string };
          setError(resBody.message ?? "Failed to update Stripe settings");
        }
      } catch {
        setError("Network error");
      } finally {
        setSaving(false);
      }
    },
    [fleetId, publishableKey, secretKey],
  );

  if (!canAdmin) {
    return (
      <section className="mb-6">
        {stripeHeader}
        <div className="bg-surface-raised border border-border rounded-lg p-6 text-center">
          <p className="text-text-secondary">Fleet admin access required.</p>
        </div>
        <StripeSetupHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
      </section>
    );
  }

  return (
    <section className="mb-6">
      {stripeHeader}

      {loading && <p className="text-text-muted text-sm">Loading Stripe settings...</p>}

      {!loading && settings && (
        <div className="bg-surface-raised border border-border rounded-lg p-6 mb-4">
          <div className="text-sm text-text-muted space-y-2 mb-4">
            <p>
              Publishable key:{" "}
              <span className="font-mono text-text-secondary">
                {settings.publishable_key ?? "Not set"}
              </span>
            </p>
            <div className="flex items-center gap-2">
              <span>Secret key:</span>
              {configuredChip(settings.secret_key_configured)}
            </div>
          </div>

          {settings.managed_by_platform ? (
            <p className="text-sm text-text-muted">
              Managed by platform environment configuration.
            </p>
          ) : (
            <form onSubmit={handleSave} className="space-y-3">
              <div>
                <label className="block text-sm text-text-secondary mb-1">Publishable Key</label>
                <input
                  type="text"
                  value={publishableKey}
                  onChange={(e) => {
                    setPublishableKey(e.target.value);
                    setSuccess(false);
                  }}
                  placeholder="pk_test_..."
                  className="w-full px-3 py-2 rounded-md bg-surface border border-border text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm text-text-secondary mb-1">Secret Key</label>
                <input
                  type="password"
                  value={secretKey}
                  onChange={(e) => {
                    setSecretKey(e.target.value);
                    setSuccess(false);
                  }}
                  placeholder="sk_test_..."
                  autoComplete="new-password"
                  className="w-full px-3 py-2 rounded-md bg-surface border border-border text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent transition-colors"
                />
              </div>

              {error && <p className="text-sm text-red-400">{error}</p>}
              {success && <p className="text-sm text-green-400">Stripe settings updated.</p>}

              <button
                type="submit"
                disabled={saving || (!publishableKey.trim() && !secretKey.trim())}
                className="px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </form>
          )}
        </div>
      )}
      <StripeSetupHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </section>
  );
}

function VenuesSection({ fleetId }: { fleetId: string }) {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchVenues = useCallback(async () => {
    try {
      const res = await apiClient["/api/v1/admin/venues"]["get"]({
        parameters: { fleet_id: fleetId },
        body: null,
      });
      if (res.code === 200) {
        setVenues(res.body as Venue[]);
      }
    } catch {
      // Ignore
    } finally {
      setLoading(false);
    }
  }, [fleetId]);

  useEffect(() => {
    fetchVenues();
  }, [fetchVenues]);

  return (
    <section className="mb-6">
      <h2 className="text-lg font-semibold text-text-primary mb-4">Venues</h2>

      {loading && <p className="text-text-muted text-sm">Loading venues...</p>}

      {!loading && venues.length === 0 && (
        <div className="bg-surface-raised border border-border rounded-lg p-6 text-center">
          <p className="text-text-secondary">No venues in this fleet yet.</p>
        </div>
      )}

      {!loading && venues.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-border">
                <th className="pb-2 text-sm font-medium text-text-muted">Name</th>
                <th className="pb-2 text-sm font-medium text-text-muted">Slug</th>
                <th className="pb-2 text-sm font-medium text-text-muted">Status</th>
              </tr>
            </thead>
            <tbody>
              {venues.map((v) => (
                <tr key={v.id} className="border-b border-border/50 hover:bg-surface-raised/50">
                  <td className="py-3">
                    <a
                      href={`/venues/${v.slug}`}
                      className="text-text-primary hover:text-accent transition-colors no-underline font-medium"
                    >
                      {v.name}
                    </a>
                  </td>
                  <td className="py-3">
                    <span className="text-sm text-text-secondary font-mono">{v.slug}</span>
                  </td>
                  <td className="py-3">{statusBadge(v.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function FleetDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();

  const [fleet, setFleet] = useState<Fleet | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  const canAdmin =
    !!user &&
    !!id &&
    (user.is_platform_admin || user.fleets.some((f) => f.id === id && f.role === "admin"));

  const fetchFleet = useCallback(async () => {
    if (!id) return;
    try {
      const res = await apiClient["/api/v1/admin/fleets/{id}"]["get"]({
        parameters: { id },
        body: null,
      });
      if (res.code === 200) {
        setFleet(res.body as Fleet);
      } else if (res.code === 403) {
        setAccessDenied(true);
      } else if (res.code === 404) {
        setNotFound(true);
      }
    } catch {
      // Ignore
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchFleet();
  }, [fetchFleet]);

  if (loading) {
    return (
      <main className="p-6">
        <p className="text-text-muted text-sm">Loading fleet...</p>
      </main>
    );
  }

  if (notFound || accessDenied || !id) {
    return (
      <main className="p-6">
        <div className="mb-6">
          <a
            href="/fleets"
            className="text-sm text-text-muted hover:text-accent transition-colors no-underline"
          >
            &larr; Back to Fleets
          </a>
        </div>
        <div className="bg-surface-raised border border-border rounded-lg p-6 text-center">
          <p className="text-text-secondary">
            {accessDenied ? "You do not have access to this fleet." : "Fleet not found."}
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="p-6">
      <div className="mb-6">
        <a
          href="/fleets"
          className="text-sm text-text-muted hover:text-accent transition-colors no-underline"
        >
          &larr; Back to Fleets
        </a>
      </div>

      {fleet && <OverviewSection fleet={fleet} canAdmin={canAdmin} onUpdated={fetchFleet} />}

      <MembersSection fleetId={id} canAdmin={canAdmin} />

      <ReportsSection fleetId={id} canAdmin={canAdmin} />

      <StripeSection fleetId={id} canAdmin={canAdmin} />

      <section className="mb-6">
        <h2 className="text-lg font-semibold text-text-primary mb-4">Pricing</h2>
        <a
          href={`/pricing?fleet_id=${id}`}
          className="text-sm text-accent hover:underline no-underline"
        >
          Manage pricing &rarr;
        </a>
      </section>

      <VenuesSection fleetId={id} />
    </main>
  );
}
